import { useMemo } from 'react';
import { fieldsToCheck, routePathsByRole } from '../constants';

export const inchesToFeetAndInches = (inches) => {
  const ft = Math.floor(inches / 12);
  const inLeft = inches % 12;
  return `${ft}'${inLeft}"`;
};

export const formatNumberWithDecimalDollar = (value) => {
  // Otherwise, use commas for thousands
  if (value <= 0) {
    return '0';
  }
  else if (0 < value && value < 1000) {
    return value.toString();
  }
  else if (1000 <= value && value < 1000 * 1000) {
    return (parseFloat(value / 1000)).toLocaleString('en-US') + 'K';
  }
  else if (1000 * 1000 <= value && value < 1000 * 1000 * 1000) {
    return (parseFloat(value / 1000 / 1000)).toLocaleString('en-US') + 'M';
  }
  else if (1000 * 1000 * 1000 <= value && value < 1000 * 1000 * 1000 * 1000) {
    return (parseFloat(value / 1000 / 1000 / 1000)).toLocaleString('en-US') + 'B';
  }
  else if (1000 * 1000 * 1000 * 1000 <= value) {
    return (parseFloat(value / 1000 / 1000 / 1000)).toLocaleString('en-US') + 'T';
  }
};

export const formatNumberWithDecimal = (value) => {
  if (value < 1 && value > 0) {
    // If between 0 and 1, remove leading 0
    return value.toFixed(1).toString().replace(/^0+/, '');
  }

  // Otherwise, use commas for thousands
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

export const formatNumbersInText = (text) => {
  return text.replace(/\b\d{4,}\b/g, (match, offset, fullText) => {
    const num = parseInt(match, 10);

    // If number looks like a year (between 1900 and 2100), skip formatting
    if (num >= 1900 && num <= 2100) {
      return match;
    }

    // Check if part of a year range (like 2022–2023)
    const before = fullText[offset - 1];
    const after = fullText[offset + match.length];
    if (before === '-' || after === '-' || before === '–' || after === '–') {
      return match;
    }

    // Otherwise, format with commas
    return num.toLocaleString();
  });
};

export const classifyFollowerQuality = (quality) => {
  if (quality === 'High') {
    return {
      level: "High",
      color: "text-bm-positive",
    };
  } else if (quality === 'Medium') {
    return {
      level: "Medium",
      color: "text-bm-attention",
    };
  } else {
    return {
      level: "Low",
      color: "text-bm-critical",
    };
  }
};

export const getPlatformKey = (url) => {
  try {
    const hostname = new URL(url).hostname;

    if (hostname.includes('hudl.com')) return 'hudl';
    if (hostname.includes('maxpreps.com')) return 'maxpreps';
    if (hostname.includes('ncsasports.org')) return 'ncsa';
    if (hostname.includes('berecruited.com')) return 'berecruited';
    if (hostname.includes('captainu.com')) return 'captainu';
    if (hostname.includes('fieldlevel.com')) return 'fieldlevel';
    if (hostname.includes('prephero.com')) return 'prephero';
    if (hostname.includes('zcruit.com')) return 'zcruit';
    if (hostname.includes('opendorse.com')) return 'opendorse';
    if (hostname.includes('inflcr.com')) return 'inflcr';
    if (hostname.includes('iconsource.com')) return 'iconsource';
    if (hostname.includes('mogl.online')) return 'mogl';
    if (hostname.includes('marketpryce.com')) return 'marketpryce';
    if (hostname.includes('ncaa.com')) return 'ncaa';
    if (hostname.includes('espn.com')) return 'espn';
    if (hostname.includes('n.rivals.com')) return 'rivals';
    if (hostname.includes('olympics.com')) return 'olympics';
    if (hostname.includes('the-passport.com')) return 'passport';

    if (hostname.includes('facebook.com')) return 'facebook';
    if (hostname.includes('linkedin.com')) return 'linkedin';
    if (hostname.includes('x.com') || hostname.includes('twitter.com')) return 'x';
    if (hostname.includes('threads.net')) return 'threads';
    if (hostname.includes('mastodon')) return 'mastodon';
    if (hostname.includes('instagram.com')) return 'instagram';
    if (hostname.includes('snapchat.com')) return 'snapchat';
    if (hostname.includes('tiktok.com')) return 'tiktok';
    if (hostname.includes('youtube.com')) return 'youtube';
    if (hostname.includes('pinterest.com')) return 'pinterest';
    if (hostname.includes('bere.al')) return 'bereal';
    if (hostname.includes('medium.com')) return 'medium';
    if (hostname.includes('tumblr.com')) return 'tumblr';
    if (hostname.includes('substack.com')) return 'substack';
    if (hostname.includes('blogger.com')) return 'blogger';
    if (hostname.includes('reddit.com')) return 'reddit';
    if (hostname.includes('twitch.tv')) return 'twitch';
    if (hostname.includes('kick.com')) return 'kick';
    if (hostname.includes('whatsapp.com')) return 'whatsapp';
    if (hostname.includes('t.me')) return 'telegram';
    if (hostname.includes('discord.com') || hostname.includes('discord.gg')) return 'discord';
    if (hostname.includes('messenger.com')) return 'messenger';
    if (hostname.includes('signal.org')) return 'signal';
    if (hostname.includes('polywork.com')) return 'polywork';
    if (hostname.includes('goodreads.com')) return 'goodreads';
    if (hostname.includes('letterboxd.com')) return 'letterboxd';
    if (hostname.includes('strava.com')) return 'strava';

    return null;
  } catch (e) {
    return null;
  }
};

export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const useCodeMaps = (codes = []) => {
  return useMemo(() => {
    if (!Array.isArray(codes)) return {};

    const map = codes.reduce((acc, code) => {
      const { type, value } = code;

      if (type == null || value == null) return acc;

      if (!acc[type]) {
        acc[type] = {};
      }

      acc[type][value] = code;
      return acc;
    }, {});

    return Object.freeze(map); // Optional: make it read-only
  }, [codes]);
};

export const isValidSocialUrl = (url) => {
  // Whitelisted platform + any real path. Profile URLs are often multi-
  // segment (hudl.com/profile/12345/Name, 247sports.com/player/...), so the
  // path just has to exist — no single-segment restriction.
  const regex = /^https?:\/\/(www\.)?(hudl|maxpreps|ncsa|berecruited|captainu|fieldlevel|prephero|zcruit|opendorse|inflcr|iconsource|mogl.online|marketpryce|ncaa|espn|n.rivals|olympics|the-passport|facebook|linkedin|x|threads|mastodon|instagram|snapchat|tiktok|youtube|pinterest|bereal|medium|tumblr|substack|blogger|reddit|twitch|kick|whatsapp|telegram|discord|messenger|signal|polywork|goodreads|letterboxd|strava|247sports|on3)\.(com|online|org)\/@?\S+$/i;
  return regex.test(url);
};

export const getProfileCompletionInfo = (athlete) => {
  const missingFields = [];
  const totalFields = Object.keys(fieldsToCheck).length;
  let filled = 0;
  Object.keys(fieldsToCheck).forEach((key) => {
    const value = athlete[key];
    const isFilled = value !== undefined && value !== null && !(typeof value === 'number' && value <= 0) && !(typeof value === 'string' && value.trim() === '') && !(typeof value ==='object' && Object.keys(value).length === 0) && !(Array.isArray(value) && value.length === 0);
    if (isFilled) {
      filled ++;
    } else {
      missingFields.push(fieldsToCheck[key]);
    }
  });
  const percentage = Math.round((filled / totalFields) * 100);
  return {
    percentage,
    missingFields,
  };
};

export const calculateProfileCompletion = (athlete) => {
  const totalFields = Object.keys(fieldsToCheck).length;
  let filled = 0;
  Object.keys(fieldsToCheck).forEach((key) => {
    const value = athlete?.[key];
    const isFilled = value !== undefined && value !== null && !(typeof value === 'number' && value <= 0) && !(typeof value === 'string' && value.trim() === '') && !(typeof value ==='object' && Object.keys(value).length === 0) && !(Array.isArray(value) && value.length === 0);
    if (isFilled) filled ++;
  });
  return Math.round((filled / totalFields) * 100);
};

export const calculateStatisticsCompletion = (user) => {
  const stats = user.athlete?.statistics || {};
  const keys = Object.keys(stats);
  if (keys.length === 0) return 0;

  let filled = 0;

  for (const key of keys) {
    const value = stats[key];

    if (key === 'final_score' && value <= 0) continue;

    if (key === 'breakdown') {
      const { 'IRL Impact': irl, 'Visibility Impact': vis, 'Sport Impact': sport } = value || {};
      if (irl <= 0 || vis <= 0 || sport <= 0) continue;
    }

    if (key === 'scores_tally') {
      const irl = value?.['IRL values']?.GPA;
      const vis = value?.['Visibility values'];
      const sports = value?.['Sports values'];

      if (irl <= 0) continue;

      if (
        vis?.['Total Followers'] <= 0 ||
        vis?.['Content'] <= 0
      ) continue;

      if (
        sports?.['Level of Play'] <= 0 ||
        sports?.['Height (inches)'] <= 0 ||
        sports?.['Weight (lbs)'] <= 0 ||
        sports?.['Sport Statistics Score'] <= 0
      ) continue;
    }

    if (
      ['normalized_final_score', 'normalized_visibility', 'normalized_success', 'weighted_sum', 'max_value', 'min_value'].includes(key)
      && value <= 0
    ) continue;

    if (key === 'team_stats') {
      const { sport_visibility, team_success, team_earnings, team_members } = value || {};
      if (sport_visibility <= 0 || team_success <= 0 || team_earnings <= 0 || team_members <= 0) continue;
    }

    if (
      ['bio', 'summaryOfThePerformanceAnalytics', 'summaryOfTheEngagementAnalytics'].includes(key)
      && value === ''
    ) continue;

    filled += 1;
  }
  return Math.round((filled / keys.length) * 100);
};

export const calculateUserRole = (userPermissions) => {
  for (const role in routePathsByRole) {
    const myRole = routePathsByRole[role]['role'];
    if (userPermissions.includes(role)) {
      return myRole;
    }
  }
};

export const extractPositionAbbreviation = (text) => {
  const match = text.match(/\((.*?)\)/);
  return match ? match[1] : text;
};

export const isValidCardNumber = (number) => {
  const digits = number.replace(/\D/g, '');
  let sum = 0, shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
     let digit = parseInt(digits[i]);
     if (shouldDouble) {
        digit *= 2; if (digit > 9) digit -= 9;
     }
     sum += digit;
     shouldDouble = !shouldDouble;
  }
  return digits.length >= 13 && digits.length <= 19 && sum % 10 === 0;
}

export const formatCardNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}


export const calculateDaysLeft = (expireDate) => {
  const expiryDate = new Date(expireDate);
  const today = new Date();

  // Calculate difference in milliseconds
  const diffTime = expiryDate - today;

  // Convert milliseconds to days (1 day = 1000 * 60 * 60 * 24 ms)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  console.log('diffDays', diffDays);

  return diffDays;
}

export const formatCurrency = (value) => {
  if (!value) return "0";
  // Remove all non-numeric characters except decimal point
  const numericValue = value.replace(/[^\d.]/g, "");

  // Split by decimal point
  const parts = numericValue.split(".");
  let integerPart = parts[0];
  let decimalPart = parts[1] || "";

  // Remove leading zeros from integer part (but leave a single 0 if empty)
  integerPart = integerPart.replace(/^0+/, "") || "0";

  // Limit integer part to 9 digits
  if (integerPart.length > 9) {
    integerPart = integerPart.slice(0, 9);
  }

  // Limit decimal part to 2 digits
  if (decimalPart.length > 2) {
    decimalPart = decimalPart.slice(0, 2);
  }

  // Check if all digits are zero or input is empty
  const isAllZeroOrEmpty = 
    (!integerPart && !decimalPart) ||
    (/^0*$/.test(integerPart) && /^0*$/.test(decimalPart));

  if (isAllZeroOrEmpty) {
    return "0";
  }

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Combine parts
  if (parts.length > 1 && decimalPart.length > 0) {
    return `${formattedInteger}.${decimalPart}`;
  } else if (parts.length > 1 && decimalPart.length === 0) {
    return `${formattedInteger}.`;
  }
  return formattedInteger;
};
